<template>
	<div class="discount-form-container">
		<Form ref="discountForm" @submit="handleSubmit">
			<!-- Station Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Station</label>
					<div class="col-12 col-md-6">
						<SelectField
							id="station"
							name="station"
							label="Select station"
						
							v-model="formData.station"
							:options="station_options"
							rules="required"
							@change="handleStationChange"
						/>
						
					</div>
				</div>
			</div>

			<!-- Battery Type Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Battery Type</label>
					<div class="col-12 col-md-6">
						<SelectField
							id="batteryType"
							name="batteryType"
							label="Select battery type"
							v-model="formData.batteryType"
							:options="batteryTypeOptions"
							rules="required"
							:disabled="!formData.station"
						/>
						
						
					</div>
				</div>
			</div>

			<!-- Battery Price Field (Auto-fetched, Disabled) -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Battery Price</label>
					<div class="col-12 col-md-6">
						<MoneyField 
							id="batteryPrice"
							name="batteryPrice"
							label="Battery price"
							v-model="formData.batteryPrice"
							currency="NGN"
							placeholder="₦0.00"
							locale="en-NG"
							:disabled="true"
						/>

						<span v-if="loading" class="d-block text-success font-italic small">
							<i class="fas fa-spinner fa-spin me-1"></i> Fetching price...
						</span>
					</div>
				</div>
			</div>

			<!-- Discounted Price Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Discounted Price</label>
					<div class="col-12 col-md-6">
						<MoneyField 
							id="discountedPrice"
							name="discountedPrice"
							label="Input discounted price"
							v-model="formData.amount"
							currency="NGN"
							placeholder="₦0.00"
							locale="en-NG"
							rules="required"
						/>
					</div>
				</div>
			</div>

			<!-- Number of Swaps Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Number of Swaps</label>
					<div class="col-12 col-md-6">
						<InputField 
							id="numberOfSwaps"
							name="numberOfSwaps"
							type="number"
							class="form-control" 
							v-model="formData.threshold"
							label="Set threshold"
							placeholder="Enter number of threshold"
							rules="required|min_value:1"
							min="1"
						/>
					</div>
				</div>
			</div>

			<!-- Effective Date Field -->
			<div class="col-md-8">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Effective Date</label>
					<div class="col-12 col-md-6">
						<DateField 
							id="date"
							name="date"
							type="date"
							class="form-control" 
							v-model="formData.date"
							label="Effective date"
							rules="date:DD-MM-YYYY"
						/>
					</div>
				</div>
			</div>

			<!-- Action Buttons -->
			<div class="col-md-8">
				<div class="d-flex gap-2 justify-content-end mt-3">
					<button 
						type="button" 
						class="btn bg-danger text-light px-4" 
						@click="handleCancel"
						:disabled="loading"
					>
						Cancel
					</button>
					<button 
						type="submit" 
						class="btn bg-secondary text-light px-4" 
						:disabled="loading || (!updateForm && !canSubmit)"
					>
						<span v-if="loading">
							<i class="fas fa-spinner fa-spin me-2"></i>Processing...
						</span>
						<span v-else>
							<i class="fas fa-plus me-2"></i>{{ updateForm ? 'Update Discount' : 'Add Discount' }}
						</span>
					</button>
				</div>
			</div>
		</Form>
	</div>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative, Emit,Watch} from 'vue-facing-decorator'
import $ from 'jquery'
import Form from '@/vue/components/Form.vue'
import SelectField from '@/vue/components/SelectField.vue'
import InputField from '@/vue/components/InputField.vue'
import MoneyField from '@/vue/components/MoneyField.vue'
import DateField from '@/vue/components/DateField.vue'
import type { Options as Select2Options } from 'select2'
import moment from 'moment'

interface DiscountFormData {
	station: string
	batteryType: string
	batteryPrice: number
	amount: number
	threshold: number
    price_id: number
	date: string
}

interface Station {
	value: string
	name: string
}

interface BatteryType {
	value: string
	name: string
	stationId?: string
}

@Component({
	components: {
		Form,
		SelectField,
		InputField,
		MoneyField,
        DateField
	}
})
class DiscountForm extends Vue {
	loading = false

	@Prop({ type: String, required: true })
	readonly url!: string

	@Prop({ type: String, required: true })
	readonly pricingUrl!: string

	@Prop({ type: Array, default: () => [] })
	readonly batteryTypes!: BatteryType[]

	@Prop({type:Object})
	readonly discountData!: any
	@Prop ({type:Boolean})
	readonly updateForm!: Boolean

	formData: DiscountFormData = {
		station: '',
		batteryType: '',
		batteryPrice: 0,
		amount: 0,
		threshold: 1,
        price_id:0,
	    date:  moment().format('DD-MM-YYYY')
	}

    get station_options(): Select2Options {
		return {
			placeholder: 'Select Station',
			minimumResultsForSearch: 3,
			ajax: {
				method: 'get',
				url: this.route('station.ajax.select.station'),
			},
			data: this.discountData?.price?.station ? [{
			id: this.discountData?.price?.station?.slug,
			text: `${this.discountData?.price?.station?.title}`,
			title: `${this.discountData?.price?.station?.title}`,
			}]:[],
			//@ts-ignore
			templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
		}
	}
	get type_options(): Select2Options {
        return {
            placeholder: 'Select Engine',
            minimumResultsForSearch: 3,
            ajax: {
                method: 'get',
                url: this.route('rider.ajax.select.engines'),
            },
		
            //@ts-ignore
            templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
            //@ts-ignore
            templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
        }
    }


	get batteryTypeOptions(): Select2Options {
		return {
			placeholder: 'Select Battery Type',
			minimumResultsForSearch: 3,
			ajax: {
				method: 'get',
				url: this.route('station.page.battery.price',{
					station: this.formData.station
				}),
			
			},
			data: this.discountData?.price?.type ? [{
			id: this.discountData?.price?.type.replace(/_/g, '-'),
			text: `${this.discountData?.price?.label}`,
			title: `${this.discountData?.price?.label}`,
			}]:[],
			 //@ts-ignore
			 templateResult: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
            //@ts-ignore
            templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),

		}
	}

	get filteredBatteryTypes(): BatteryType[] {
		if (!this.formData.station) {
			return []
		}
		return this.batteryTypes.filter(type => 
			!type.stationId || type.stationId === this.formData.station
		)
	}

	get canSubmit(): boolean {
		return !!(
			this.formData.station &&
			this.formData.batteryType &&
			this.formData.batteryPrice > 0 &&
			this.formData.amount > 0 &&
			this.formData.threshold > 0 &&
			this.formData.date
		)
	}

	async handleStationChange() {
		this.formData.batteryType = ''
		this.formData.batteryPrice = 0
		this.formData.amount = 0
	}
	@Watch('formData.batteryType')
	 onhandleBatteryTypeChange(value: string, oldValue: string) {
		if (!oldValue && this.updateForm) return;
		if (this.formData.station && value) {
			return this.fetchBatteryPrice()
		}
	}

	async fetchBatteryPrice() {

		if (!this.formData.station || !this.formData.batteryType) {
				this.formData.price_id = 0;
				this.formData.batteryPrice = 0;
				return;
			}

		try {

			this.loading = true
			const response = await this.$http.post(this.pricingUrl, {
				
					station: this.formData.station,
					batteryType: this.formData.batteryType.replace(/-/g, '_')
				
			})
			
			if (response?.data?.pricing) {

                this.formData.price_id = response.data.price_id
				this.formData.batteryPrice = response.data.pricing
				this.loading = false

			}
            else {
            this.formData.price_id = 0;
            this.formData.batteryPrice = 0;
			this.loading = false;
            }
		} catch (error) {
			this.loading = false
			//console.error('Failed to fetch battery price:', error)
			this.onError({
				message: 'Failed to fetch battery price',
				error
			})
		}
	}

	 created() {
    this.$nextTick();
    if (this.updateForm && this.discountData) {
        // Wait a bit longer for Select2 to fully initialize
        setTimeout(() => {
            this.prefillForm();
        }, 100);
    }
}

prefillForm() {
    if (this.updateForm && this.discountData) {
        const d = this.discountData;
       
        // Map all data first
        this.formData.price_id = d.price.id;
        this.formData.amount = d.amount.amount / 100; 
        this.formData.threshold = d.threshold;
        this.formData.batteryPrice = d.price.amount.amount / 100;
        this.formData.batteryType = d.price.type.replace(/_/g, '-');
		this.formData.station = d.price.station.slug;
        // Handle date
        if (d.activated_at) {
            this.formData.date = moment(d.activated_at).format('DD-MM-YYYY');
        }
    }
}
	async handleSubmit(payload: any) {
		try {
			this.setLoading(true)

            const submissionData = {
            price_id: this.formData.price_id,
            amount: this.formData.amount * 100, // kobo
            threshold: this.formData.threshold,
            date: this.formData.date // Make sure it's in `d-m-Y` format
        };

			

			const response = this.updateForm ? await this.$http.put(this.url, submissionData) : await this.$http.post(this.url, submissionData)

			this.onSuccess(response)
			this.resetForm()

		} catch (error) {
			this.onError(error)
		} finally {
			this.setLoading(false)
		}
	}

	@Emit('success')
	onSuccess(response: any) {
		return {
			message: response?.data?.message || 'Discount added successfully!',
			data: response?.data
		}
	}

	@Emit('error')
	onError(error: any) {
		return {
			message: error.response?.data?.message || 'Failed to add discount. Please try again.',
			error
		}
	}

	setLoading(state: boolean) {
		return this.loading = state
	}

	@Emit('cancel')
	handleCancel() {
		if (!this.loading) { 
			this.resetForm()
			//this.router.visit(this.route('discount.page.group'))
			return true
		}
		return false
	}
	formatDate(date: string): string {
		return new Date(date).toLocaleDateString('en-NG', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		})
	}

	resetForm() {
		this.formData = {
			station: '',
			batteryType: '',
			batteryPrice: 0,
			amount: 0,
			threshold: 1,
            price_id:0,
			date: ''
		}
	}
}

export default toNative(DiscountForm)
</script>

<style scoped>
.discount-form-container {
	width: 100%;
}

.form-label {
	margin-bottom: 0.5rem;
	color: #6c757d;
}

@media (max-width: 768px) {
	.col-md-6 {
		margin-bottom: 0.5rem;
	}
}
</style>